import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ArrowLeft, 
  Download, 
  Search, 
  Calendar, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  DollarSign,
  CreditCard,
  Clock,
  Filter,
  ChevronUp,
  ChevronDown
} from "lucide-react";
import "../../Styling/hrDashboard.css";

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Add this

   const { defaultTab = 'overview', filters = {} } = location.state || {};
  
  // Add state for active tab
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  // Mock employee data for October (from your Excel)
  const [employees, setEmployees] = useState([
    {
      id: 2,
      name: "Raj Kumar Thakur",
      designation: "Manager",
      department: "Plant",
      basicSalary: 20000,
      allowances: 29750, // HRA + MA + TA + Spl All.
      deductions: 0,
      netPayableSalary: 49750,
      status: "pending"
    },
    {
      id: 3,
      name: "Manglam Kumar",
      designation: "Cordinator",
      department: "Operation",
      basicSalary: 12000,
      allowances: 8000, // HRA + Spl All.
      deductions: 0,
      netPayableSalary: 20000,
      status: "pending"
    },
    {
      id: 5,
      name: "Nibha Kumari",
      designation: "Executive",
      department: "Marketing",
      basicSalary: 20000,
      allowances: 20750, // HRA + MA + Spl All.
      deductions: 0,
      netPayableSalary: 40750,
      status: "pending"
    },
    {
      id: 6,
      name: "Rishabh Singh",
      designation: "Cordinator",
      department: "Production",
      basicSalary: 12000,
      allowances: 3000, // HRA
      deductions: 0,
      netPayableSalary: 15000,
      status: "paid"
    },
    {
      id: 7,
      name: "Aditi",
      designation: "Graphic Designer",
      department: "Marketing",
      basicSalary: 20000,
      allowances: 20750, // HRA + MA + Spl All.
      deductions: 0,
      netPayableSalary: 40750,
      status: "pending"
    },
    {
      id: 8,
      name: "Vidisha",
      designation: "BD&BA",
      department: "Marketing",
      basicSalary: 60000,
      allowances: 90000, // HRA + MA + TA + Spl All.
      deductions: 0,
      netPayableSalary: 150000,
      status: "paid"
    }
  ]);

  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(activeTab === 'pending' ? 'pending' : 'all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedMonth, setSelectedMonth] = useState("2025-10");
  const [notification, setNotification] = useState("");

  // Stats calculation
  const stats = {
    totalSalary: employees.reduce((sum, emp) => sum + emp.netPayableSalary, 0),
    paidSalary: employees.filter(emp => emp.status === "paid").reduce((sum, emp) => sum + emp.netPayableSalary, 0),
    pendingSalary: employees.filter(emp => emp.status === "pending").reduce((sum, emp) => sum + emp.netPayableSalary, 0),
    totalEmployees: employees.length,
    paidEmployees: employees.filter(emp => emp.status === "paid").length,
    pendingEmployees: employees.filter(emp => emp.status === "pending").length
  };

    useEffect(() => {
    if (activeTab === 'paid') {
      setStatusFilter('paid');
    } else if (activeTab === 'pending') {
      setStatusFilter('pending');
    } else {
      setStatusFilter('all');
    }
  }, [activeTab]);

  // Filter and sort employees
  useEffect(() => {
    let filtered = employees;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchLower) ||
        emp.designation.toLowerCase().includes(searchLower) ||
        emp.department.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(emp => emp.status === statusFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredEmployees(filtered);
  }, [employees, searchTerm, statusFilter, sortConfig]);

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }; 
  

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <div className="sort-icons">
          <ChevronUp size={12} className="sort-icon" />
          <ChevronDown size={12} className="sort-icon" />
        </div>
      );
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp size={14} className="sort-icon active" /> : 
      <ChevronDown size={14} className="sort-icon active" />;
  };

  // Approve salary payment
  const approveSalary = (employeeId) => {
    setEmployees(prev => prev.map(emp => 
      emp.id === employeeId ? { ...emp, status: "paid" } : emp
    ));

    const employee = employees.find(emp => emp.id === employeeId);
    setNotification(`Salary successfully credited to ${employee.name}`);
    
    // Clear notification after 3 seconds
    setTimeout(() => setNotification(""), 3000);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return { 
          text: "Paid", 
          color: "#10b981", 
          icon: <CheckCircle size={14} /> 
        };
      case 'pending':
        return { 
          text: "Pending", 
          color: "#f59e0b", 
          icon: <Clock size={14} /> 
        };
      default:
        return { 
          text: status, 
          color: "#6b7280", 
          icon: <AlertCircle size={14} /> 
        };
    }
  };

  return (
    <div className="payroll-dashboard-container">
      {/* Header */}
      <div className="payroll-header">
        <div className="payroll-header-main">
          <button 
            className="back-button"
            onClick={() => navigate('/hr-dashboard')}
          >
            <ArrowLeft size={20} />
            Back to HR Dashboard
          </button>
          <div className="header-title-section">
            <h1>Payroll Management</h1>
            <p>Salary calculation and approval for {selectedMonth}</p>
          </div>
        </div>
        
        <div className="header-actions">
          <button className="export-btn">
            <Download size={16} />
            Export Payroll
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className="success-notification">
          <CheckCircle size={18} />
          {notification}
        </div>
      )}

      {/* Stats Cards - CLICKABLE */}
      <div className="payroll-stats-grid">
        <div 
          className="stat-card total-salary"
          onClick={() => setStatusFilter("all")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{formatCurrency(stats.totalSalary)}</div>
            <div className="stat-label">Total Salary</div>
            <div className="stat-subtext">{stats.totalEmployees} employees</div>
          </div>
        </div>
        
        <div 
          className="stat-card paid-salary"
          onClick={() => setStatusFilter("paid")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <CreditCard size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{formatCurrency(stats.paidSalary)}</div>
            <div className="stat-label">Paid Salary</div>
            <div className="stat-subtext">{stats.paidEmployees} employees</div>
          </div>
        </div>
        
        <div 
          className="stat-card pending-salary"
          onClick={() => setStatusFilter("pending")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{formatCurrency(stats.pendingSalary)}</div>
            <div className="stat-label">Pending Salary</div>
            <div className="stat-subtext">{stats.pendingEmployees} employees</div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="payroll-filters">
        <div className="filter-group">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by name, designation, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-group">
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending Approval</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        
        <div className="filter-group">
          <div className="date-filter-wrapper">
            <Calendar size={16} className="date-filter-icon" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="filter-input date-input"
            />
          </div>
        </div>
      </div>

      {/* Salary Calculation Dashboard */}
      <div className="salary-section">
        <h2>Salary Calculation - October 2025</h2>
        <div className="table-scroll-wrapper">
          <table className="salary-table">
            <thead>
              <tr>
                <th>Emp ID</th>
                <th className="sortable-header" onClick={() => handleSort('name')}>
                  <div className="header-content">
                    Employee Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th>Designation</th>
                <th>Department</th>
                <th className="sortable-header" onClick={() => handleSort('basicSalary')}>
                  <div className="header-content">
                    Basic Salary
                    {getSortIcon('basicSalary')}
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('allowances')}>
                  <div className="header-content">
                    Allowances
                    {getSortIcon('allowances')}
                  </div>
                </th>
                <th>Deductions</th>
                <th className="sortable-header" onClick={() => handleSort('netPayableSalary')}>
                  <div className="header-content">
                    Net Payable
                    {getSortIcon('netPayableSalary')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(employee => (
                <tr key={employee.id}>
                  <td>#{employee.id}</td>
                  <td>
                    <div className="employee-info">
                      <div className="employee-name">{employee.name}</div>
                    </div>
                  </td>
                  <td>{employee.designation}</td>
                  <td>{employee.department}</td>
                  <td>{formatCurrency(employee.basicSalary)}</td>
                  <td>{formatCurrency(employee.allowances)}</td>
                  <td>{formatCurrency(employee.deductions)}</td>
                  <td className="net-payable">{formatCurrency(employee.netPayableSalary)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Approval Section */}
      <div className="approval-section">
        <h2>Salary Payment Approval</h2>
        <div className="table-scroll-wrapper">
          <table className="approval-table">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Net Payable Salary</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees
                .filter(emp => emp.status === "pending")
                .map(employee => {
                  const status = getStatusBadge(employee.status);
                  return (
                    <tr key={employee.id}>
                      <td>
                        <div className="employee-info">
                          <div className="employee-name">{employee.name}</div>
                          <div className="employee-details">{employee.designation} • {employee.department}</div>
                        </div>
                      </td>
                      <td className="net-payable">{formatCurrency(employee.netPayableSalary)}</td>
                      <td>
                        <div 
                          className="status-badge"
                          style={{ 
                            backgroundColor: `${status.color}15`, 
                            color: status.color, 
                            borderColor: status.color 
                          }}
                        >
                          {status.icon}
                          {status.text}
                        </div>
                      </td>
                      <td>
                        <button 
                          className="approve-btn"
                          onClick={() => approveSalary(employee.id)}
                        >
                          <CheckCircle size={16} />
                          Approve Payment
                        </button>
                      </td>
                    </tr>
                  );
                })}
              
              {filteredEmployees.filter(emp => emp.status === "pending").length === 0 && (
                <tr>
                  <td colSpan="4" className="no-pending">
                    <CheckCircle size={24} />
                    <span>No pending salary approvals</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayrollDashboard;